import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

// Les tables site_banners / product_offers ne sont pas dans le type Database généré :
// on passe par un client non typé pour ces requêtes (lecture publique via RLS).
const db = supabase as any;

export interface SiteBanner {
  id: string;
  type: string;
  title?: string | null;
  message?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  deadline?: string | null;
  show_countdown?: boolean;
  bg_color?: string | null;
  text_color?: string | null;
  pages?: string[] | null;
  active?: boolean;
  display_order?: number;
}

export interface ProductOffer {
  id: string;
  product_id?: string | null;
  title?: string | null;
  badge?: string | null;
  valid_until?: string | null;
  discount_text?: string | null;
  benefits?: string[] | null;
  cta_primary_label?: string | null;
  cta_secondary_label?: string | null;
  reservations_count?: number | null;
  stock_reserved_pct?: number | null;
  show_progress?: boolean;
  payment_note?: string | null;
  active?: boolean;
  display_order?: number;
}

/**
 * Bandeau actif pour une page donnée ('home' | 'product' | ...).
 * Retourne null si aucun bandeau actif / erreur => le composant garde son fallback.
 */
export function useSiteBanner(page: string) {
  const [banner, setBanner] = useState<SiteBanner | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    db.from('site_banners')
      .select('*')
      .eq('active', true)
      .order('display_order', { ascending: true })
      .then(({ data, error }: { data: SiteBanner[] | null; error: any }) => {
        if (!active) return;
        if (error) { setReady(false); setLoading(false); return; }
        const match = (data || []).find(
          (b) => !b.pages || b.pages.length === 0 || b.pages.includes(page),
        );
        setBanner(match || null);
        setReady(true);
        setLoading(false);
      })
      .then(undefined, () => { if (active) { setReady(false); setLoading(false); } });
    return () => { active = false; };
  }, [page]);

  return { banner, loading, ready };
}

/**
 * Offre active pour un produit (par uuid) sinon offre globale (product_id null).
 * Retourne null si aucune offre / erreur => le composant garde son fallback.
 */
export function useProductOffer(productUuid?: string | null) {
  const [offer, setOffer] = useState<ProductOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    db.from('product_offers')
      .select('*')
      .eq('active', true)
      .order('display_order', { ascending: true })
      .then(({ data, error }: { data: ProductOffer[] | null; error: any }) => {
        if (!active) return;
        if (error) { setReady(false); setLoading(false); return; }
        const rows = data || [];
        const specific = productUuid ? rows.find((o) => o.product_id === productUuid) : undefined;
        const global = rows.find((o) => !o.product_id);
        setOffer(specific || global || null);
        setReady(true);
        setLoading(false);
      })
      .then(undefined, () => { if (active) { setReady(false); setLoading(false); } });
    return () => { active = false; };
  }, [productUuid]);

  return { offer, loading, ready };
}

export interface HomePersona {
  id: string;
  key?: string | null;
  icon: string;
  title: string;
  subtitle?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  active?: boolean;
  display_order?: number;
}

export interface SimulatorSettings {
  id: string;
  eyebrow?: string | null;
  title?: string | null;
  subtitle?: string | null;
  weeks_per_month: number | string;
  default_km: number;
  default_days: number;
  default_fuel: number;
  default_recharge: number;
  co2_factor_g: number | string;
  result_label?: string | null;
  bullets?: string[] | null;
  image_url?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  secondary_label?: string | null;
  secondary_url?: string | null;
  active?: boolean;
}

/** Profils (segmentation) de la page d'accueil — table home_personas. */
export function useHomePersonas() {
  const [personas, setPersonas] = useState<HomePersona[] | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let active = true;
    db.from('home_personas')
      .select('*')
      .eq('active', true)
      .order('display_order', { ascending: true })
      .then(({ data, error }: { data: HomePersona[] | null; error: any }) => {
        if (!active) return;
        if (error) { setReady(false); return; }
        setPersonas(data || []);
        setReady(true);
      })
      .then(undefined, () => { if (active) setReady(false); });
    return () => { active = false; };
  }, []);
  return { personas, ready };
}

/** Paramètres du simulateur d'économies — table simulator_settings (ligne unique). */
export function useSimulatorSettings() {
  const [settings, setSettings] = useState<SimulatorSettings | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let active = true;
    db.from('simulator_settings')
      .select('*')
      .eq('active', true)
      .limit(1)
      .then(({ data, error }: { data: SimulatorSettings[] | null; error: any }) => {
        if (!active) return;
        if (error) { setReady(false); return; }
        setSettings((data && data[0]) || null);
        setReady(true);
      })
      .then(undefined, () => { if (active) setReady(false); });
    return () => { active = false; };
  }, []);
  return { settings, ready };
}
